import React, { useState, useEffect } from 'react';
import { Upload, User, Lock } from 'lucide-react';
import config from '../../config';
import { StepProps } from '../common/types';

export const PersonalInfoTab: React.FC<StepProps> = ({
  formik,
  isEdit
}) => {
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const isEditMode = isEdit ?? false;

  useEffect(() => {
    if (formik.values.photo && typeof formik.values.photo === 'string') {
      setPreviewImage(`${config.apiBaseUrl}/user-profile-image/${formik.values.photo}`);
    }
  }, [formik.values.photo]);

  // 🔥 Handle local upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("Uploading img//////////");
    const file = e.target.files?.[0];
    if (file) {
      formik.setFieldValue("photo", file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-xl font-semibold text-gray-900">Basic Information</h3>
        <p className="mt-1 text-sm text-gray-600">
          Provide the employee's personal details and profile information
          {isEditMode && (
            <span className="ml-2 text-blue-600 font-medium">
              (Name and email cannot be changed in edit mode)
            </span>
          )}
        </p>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">

        {/* Profile Image Section - Left Side */}
        <div className="lg:col-span-4 xl:col-span-3">
          <div className="bg-gray-50 rounded-xl p-6 sticky top-6">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Profile Image
            </label>

            <div className="flex flex-col items-center space-y-4">
              {/* Image Preview */}
              <div className="relative group">
                <div className="w-32 h-32 sm:w-40 sm:h-40 bg-white rounded-2xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden shadow-sm transition-all duration-200 group-hover:border-blue-400">
                  {previewImage ? (
                    <img
                      src={previewImage}
                      alt="Profile"
                      className="w-full h-full object-cover rounded-2xl"
                    />
                  ) : (
                    <div className="text-gray-400 text-center p-4">
                      <User className="w-12 h-12 mx-auto mb-2 opacity-60" />
                      <span className="text-sm block">No image</span>
                    </div>
                  )}
                </div>

                {/* Upload Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-2xl transition-all duration-200 flex items-center justify-center">
                  <Upload className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </div>
              </div>

              {/* Upload Button */}
              <div className="w-full">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="profile-image"
                />
                <label
                  htmlFor="profile-image"
                  className="w-full bg-white text-gray-700 px-4 py-3 rounded-lg text-sm font-medium cursor-pointer border border-gray-300 hover:border-blue-500 hover:text-blue-600 transition-all duration-200 flex items-center justify-center gap-2 shadow-sm"
                >
                  <Upload className="w-4 h-4" />
                  Select Image
                </label>
                <p className="text-xs text-gray-500 text-center mt-2">
                  JPG, PNG or WEBP. Max 5MB.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Fields Section - Right Side */}
        <div className="lg:col-span-8 xl:col-span-9">
          <div className="space-y-6">

            {/* Employee Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                Employee Name *
                {isEditMode && <Lock className="w-4 h-4 text-gray-400" />}
              </label>
              <input
                type="text"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                placeholder="Enter employee name"
                disabled={isEditMode}
                className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 placeholder-gray-400 ${isEditMode
                  ? 'bg-gray-100 text-gray-600 cursor-not-allowed border-gray-200'
                  : 'bg-white'
                  }`}
              />
              {isEditMode && (
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Name cannot be changed in edit mode
                </p>
              )}
              {formik.touched.name && formik.errors.name && (
                <div className="text-red-600 text-sm mt-2 flex items-center gap-1">
                  <span>⚠</span> {formik.errors.name}
                </div>
              )}
            </div>

            {/* Email Field - Only show in edit mode since it's not in the original form */}
            {isEditMode && formik.values.email && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  Email Address
                  <Lock className="w-4 h-4 text-gray-400" />
                </label>
                <input
                  type="email"
                  value={formik.values.email}
                  disabled
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed"
                  placeholder="Email address"
                />
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Email cannot be changed in edit mode
                </p>
              </div>
            )}

            {/* Radio Groups Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Gender */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Gender *
                </label>
                <div className="flex flex-wrap gap-4">
                  {['Male', 'Female'].map((gender) => (
                    <label key={gender} className="flex items-center space-x-2 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="radio"
                          name="gender"
                          value={gender}
                          checked={formik.values.gender === gender}
                          onChange={formik.handleChange}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900">
                        {gender}
                      </span>
                    </label>
                  ))}
                </div>
                {formik.touched.gender && formik.errors.gender && (
                  <div className="text-red-600 text-sm mt-2 flex items-center gap-1">
                    <span>⚠</span> {formik.errors.gender}
                  </div>
                )}
              </div>

              {/* Work Type */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Work Type *
                </label>
                <div className="flex flex-wrap gap-4">
                  {['Office', 'OnField'].map((workType) => (
                    <label key={workType} className="flex items-center space-x-2 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="radio"
                          name="work_type"
                          value={workType}
                          checked={formik.values.work_type === workType}
                          onChange={formik.handleChange}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900">
                        {workType}
                      </span>
                    </label>
                  ))}
                </div>
                {formik.touched.work_type && formik.errors.work_type && (
                  <div className="text-red-600 text-sm mt-2 flex items-center gap-1">
                    <span>⚠</span> {formik.errors.work_type}
                  </div>
                )}
              </div>
            </div>

            {/* Dates Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                <input
                  name="dob"
                  type="date"
                  value={formik.values.dob || ''}
                  onChange={formik.handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
              </div>

              {/* Marital Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marital Status
                </label>
                <select
                  name="marital_status"
                  value={formik.values.marital_status}
                  onChange={formik.handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
                >
                  <option value="">Select Status</option>
                  <option value="unmarried">Single</option>
                  <option value="married">Married</option>
                </select>
              </div>

              {/* Anniversary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Anniversary
                </label>
                <input
                  name="anniversary"
                  type="date"
                  value={formik.values.anniversary}
                  onChange={formik.handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};